import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Scrolls to a section by id and syncs the URL hash. Use with targets that set scroll-margin-top for fixed headers.
 */
export const scrollToElementById = (id: string) => {
  if (typeof document === "undefined") {
    return;
  }
  const el = document.getElementById(id);
  if (!el) {
    return;
  }
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  window.history.replaceState(null, "", `#${id}`);
};

/**
 * Flattens a nested object into dot-notation keys; all values are stringified.
 */
export function flattenObject(input?: unknown): Record<string, string> {
  if (input === undefined) {
    return {};
  }
  if (input === null || typeof input !== "object") {
    return {};
  }

  const result: Record<string, string> = {};

  const walk = (value: unknown, path: string): void => {
    if (value === null) {
      result[path] = "null";
      return;
    }
    if (value === undefined) {
      result[path] = "undefined";
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item, i) => {
        const key = path ? `${path}.${i}` : String(i);
        walk(item, key);
      });
      return;
    }
    if (typeof value === "object") {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        const key = path ? `${path}.${k}` : k;
        if (Array.isArray(v)) {
          walk(v, key);
        } else if (v !== null && typeof v === "object") {
          walk(v, key);
        } else {
          walk(v, key);
        }
      }
      return;
    }
    result[path] = String(value);
  };

  walk(input, "");
  return result;
}
